export type Observer<T> = (data: T, previousData: T | null) => void;

/**
 * A class that can notify observers of changes.
 *
 * @example
 * ```ts
 * // Class with an observable property
 * class Test extends SimpleObservableObservable {
 *   public click = new SimpleObservableObservableSubject<string>();
 *
 *   public onClick() {
 *     this.notify(this.click, "test");
 *   }
 * }
 *
 * // Then on the observer:
 * const test = new Test();
 * test.click.subscribe((data) => {
 *   console.log(data); // "test"
 * });
 *
 */
export abstract class SimpleObservable {
  protected notify<T>(subject: SimpleObservableSubject<T>, data: T) {
    subject[notifyObservableSymbol](data);
  }
}

export function notifySubject<T>(subject: SimpleObservableSubject<T>, data: T) {
  subject[notifyObservableSymbol](data);
}

/**
 * Makes the notify method for `SimpleObservableSubject` private to this module.
 */
const notifyObservableSymbol = Symbol("notifyObservable");

/**
 * A subject that can be observed by multiple observers.
 *
 * Use in conjunction with the `SimpleObservable` class.
 */
export class SimpleObservableSubject<T> {
  protected observers: Set<Observer<T>>;
  protected canHoldState: boolean;
  protected onlyNotifyOnChanges: boolean;

  protected lastState: { data: T } | null = null;

  constructor(
    options: {
      observers?: Set<Observer<T>>;
      canHoldState?: boolean;
      onlyNotifyOnChanges?: boolean;
    } = {},
  ) {
    const {
      observers = new Set(),
      canHoldState = true,
      onlyNotifyOnChanges = false,
    } = options;
    this.observers = observers;
    this.canHoldState = canHoldState;
    this.onlyNotifyOnChanges = onlyNotifyOnChanges;
  }

  subscribe(
    observer: Observer<T>,
    { notifyOnSubscribe = true }: { notifyOnSubscribe?: boolean } = {},
  ) {
    this.observers.add(observer);

    if (notifyOnSubscribe && this.lastState) {
      observer(this.lastState.data, null);
    }

    return () => this.unsubscribe(observer);
  }

  unsubscribe(observer: Observer<T>) {
    this.observers.delete(observer);
  }

  clear() {
    this.observers.clear();
  }

  [notifyObservableSymbol](data: T) {
    const oldData = this.lastState?.data;

    if (this.onlyNotifyOnChanges && oldData === data) {
      return;
    }

    this.lastState = { data };
    for (const observer of this.observers) {
      try {
        observer(data, oldData ?? null);
      } catch (error) {
        console.error(error);
      }
    }
  }
}

export class SimpleObservableValue<T> extends SimpleObservableSubject<T> {
  protected defaultValue: T;

  constructor(defaultValue: T) {
    super();
    this.defaultValue = defaultValue;
  }

  get value(): T {
    return this.lastState?.data ?? this.defaultValue;
  }

  set value(value: T) {
    this.lastState = { data: value };
    this[notifyObservableSymbol](value);
  }
}
